// Retina rendering: the canvas is sized at device-pixel resolution (capped) and every
// scene's camera is zoomed by the same factor from the top-left corner, so the world
// stays 1280x720 and scene code, input and DOM overlays need no changes.
// DPR resolution + canvas sizing live in ./dpr.js (pure, Node-loadable); this module is
// the Phaser-side installer and imports 'phaser' at module scope, so it must never be
// imported by `node --test`.

import Phaser from 'phaser';
import { DPR } from './dpr.js';

let textWrapped = false;

// Every Text rasterises at style.resolution (Phaser 3.90 has no global setting; default 1).
// Wrap updateText once so all 150+ call sites get crisp glyphs without changes.
function wrapTextResolution(Phaser) {
  if (textWrapped) return;
  textWrapped = true;
  const original = Phaser.GameObjects.Text.prototype.updateText;
  Phaser.GameObjects.Text.prototype.updateText = function () {
    if (!this.__retina) { this.__retina = true; this.style.resolution = DPR; }
    return original.call(this);
  };
}

function zoomCamera(scene) {
  const cam = scene.cameras?.main;
  if (!cam) return;
  cam.setOrigin(0, 0);   // zoom from the top-left: world (0,0) stays at canvas (0,0)
  cam.setZoom(DPR);
}

let sceneStartWrapped = false;

// Zoom every scene's camera the moment it starts, from the very first scene onward
// (including BootScene, so its loading bar is drawn zoomed, not 1x-then-jarringly-rescaled).
//
// The brief's original approach — iterate `game.scene.scenes` right after `new
// Phaser.Game(...)` and attach a `scene.events.on(Phaser.Scenes.Events.START, ...)`
// listener to each — does not work: Phaser only moves scenes from its internal
// `_pending` queue into `game.scene.scenes` inside `SceneManager.bootQueue`, which runs
// on the game's `READY` event (asynchronous, after `new Phaser.Game()` has already
// returned). So `game.scene.scenes` is still empty at the point `installRetina` runs,
// the loop attaches zero listeners, and no scene — including the very first one — ever
// gets zoomed. Confirmed by reading node_modules/phaser/src/scene/SceneManager.js and by
// observing zoom stuck at 1 / originX stuck at 0.5 in the running game.
//
// Instead, wrap `Phaser.Scenes.Systems.prototype.start` once: it is the function every
// scene start (present and future, boot-time or `scene.start()` at runtime) funnels
// through to emit `Phaser.Scenes.Events.START`, so hooking it has the identical effect
// (fires before `preload`/`create`) without depending on scene-registration timing.
function wrapSceneStart(Phaser) {
  if (sceneStartWrapped) return;
  sceneStartWrapped = true;
  const original = Phaser.Scenes.Systems.prototype.start;
  Phaser.Scenes.Systems.prototype.start = function (data) {
    const result = original.call(this, data);
    zoomCamera(this.scene);
    return result;
  };
}

// Call once after `new Phaser.Game(...)`.
export function installRetina(_game) {
  if (typeof window !== 'undefined') window.__DPR = DPR;
  if (DPR === 1) return;   // non-retina screens keep the exact old path
  wrapTextResolution(Phaser);
  wrapSceneStart(Phaser);
}
